import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Crown, Check, Users, Calendar, Tag, Target, BarChart, Mail, Eye, FileText, Building2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Membership = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, refreshUser, isPremium, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [benefits, setBenefits] = useState(null);
  const [myPrice, setMyPrice] = useState(null);
  const [businessPricing, setBusinessPricing] = useState(null);

  useEffect(() => {
    fetchBenefits();
    fetchBusinessPricing();
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (user && token) {
      fetchMyPrice();
    }
  }, [user, token]);

  const fetchBenefits = async () => {
    try {
      const response = await axios.get(`${API}/membership-benefits`);
      setBenefits(response.data);
    } catch (error) {
      console.error('Error fetching benefits:', error);
    }
  };

  const fetchMyPrice = async () => {
    try {
      const response = await axios.get(`${API}/membership/my-price`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyPrice(response.data);
    } catch (error) {
      console.error('Error fetching my price:', error);
    }
  };

  const fetchBusinessPricing = async () => {
    try {
      const response = await axios.get(`${API}/membership/business-pricing`);
      setBusinessPricing(response.data);
    } catch (error) {
      console.error('Error fetching business pricing:', error);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          toast.success('Payment successful! Your membership is now active.');
          await refreshUser();
          navigate('/membership', { replace: true });
        } else if (response.data.status === 'expired') {
          toast.error('Payment session expired. Please try again.');
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        }
      } catch (error) {
        toast.error('Error checking payment status');
      } finally {
        setCheckingPayment(false);
      }
    };

    poll();
  };

  const handleSubscribe = async (membershipType) => {
    if (!user) {
      toast.error('Please login to subscribe');
      navigate('/login');
      return;
    }

    if (membershipType === 'attendee_premium' && user.user_type !== 'attendee') {
      toast.error('Attendee membership is for attendee accounts. Please create an attendee account.');
      return;
    }
    if (membershipType === 'business_premium' && user.user_type !== 'business') {
      toast.error('Business membership is for business accounts. Please create a business account.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/checkout/membership?membership_type=${membershipType}`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating checkout session');
      setLoading(false);
    }
  };

  if (authLoading || checkingPayment) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-muted-foreground">
            {checkingPayment ? 'Verifying payment...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="container-custom max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="overline text-gold mb-4">Membership</p>
          <h1 className="text-4xl md:text-5xl mb-4">Choose Your Plan</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Start with our complimentary membership and upgrade when you need more. All plans include core features to connect with the aging care community.
          </p>
        </div>

        {isPremium() && (
          <div className="mb-12 p-6 border-2 border-gold bg-gold/10 text-center">
            <Crown className="w-8 h-8 text-gold mx-auto mb-3" />
            <h3 className="text-xl mb-2">You're a Premium Member!</h3>
            <p className="text-muted-foreground">
              Your {user.membership_tier === 'attendee_premium' ? 'Attendee' : 'Business'} Premium membership is active until{' '}
              {new Date(user.membership_expires).toLocaleDateString()}
            </p>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-8">
          {/* Complimentary Tier */}
          <div className="border border-border/50 p-8 bg-card">
            <h3 className="text-xl mb-2">Complimentary</h3>
            <p className="text-3xl font-semibold mb-2">$0<span className="text-sm text-muted-foreground">/month</span></p>
            <p className="text-sm text-muted-foreground mb-6">Perfect for getting started</p>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <Eye className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Browse and view all public events</span>
              </li>
              <li className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">RSVP to complimentary events</span>
              </li>
              <li className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Post basic event listings</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Basic profile</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">RSVP confirmation emails with calendar invites</span>
              </li>
            </ul>
            {(!user || user.membership_tier === 'free') ? (
              <Button 
                variant="outline" 
                className="w-full rounded-sm uppercase tracking-widest"
                disabled
              >
                {user ? 'Current Plan' : 'Join Free'}
              </Button>
            ) : (
              <Button 
                variant="outline" 
                className="w-full rounded-sm uppercase tracking-widest"
                disabled
              >
                Included
              </Button>
            )}
          </div>

          {/* Attendee Premium */}
          <div className="border border-border/50 p-8 bg-card">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-gold" />
              <h3 className="text-xl">Attendee Premium</h3>
            </div>
            <p className="text-3xl font-semibold mb-2">$5<span className="text-sm text-muted-foreground">/month</span></p>
            <p className="text-sm text-muted-foreground mb-6">For networking professionals</p>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3 text-muted-foreground">
                <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span>Everything in Complimentary, plus:</span>
              </li>
              <li className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Early access to events (48hr head start)</span>
              </li>
              <li className="flex items-start gap-3">
                <Crown className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Premium member badge</span>
              </li>
              <li className="flex items-start gap-3">
                <Users className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Member directory access</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Priority spots for popular events</span>
              </li>
              <li className="flex items-start gap-3">
                <Tag className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Event discount codes</span>
              </li>
            </ul>
            {user?.membership_tier === 'attendee_premium' ? (
              <Button 
                variant="outline" 
                className="w-full rounded-sm uppercase tracking-widest border-gold text-gold"
                disabled
              >
                Current Plan
              </Button>
            ) : (
              <Button 
                onClick={() => handleSubscribe('attendee_premium')}
                disabled={loading || user?.user_type === 'business'}
                className="w-full bg-gold text-primary hover:bg-gold/90 rounded-sm uppercase tracking-widest"
                data-testid="subscribe-attendee-btn"
              >
                {loading ? <span className="spinner mr-2" /> : null}
                {user?.user_type === 'business' ? 'For Attendees Only' : 'Subscribe'}
              </Button>
            )}
          </div>

          {/* Business Premium */}
          <div className="border-2 border-gold p-8 bg-card relative">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-primary text-xs px-3 py-1 uppercase tracking-wider">Popular</span>
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-gold" />
              <h3 className="text-xl">Business Premium</h3>
            </div>
            
            {/* Show personalized pricing for logged in business users */}
            {user?.user_type === 'business' && myPrice?.special_pricing ? (
              <>
                <p className="text-3xl font-semibold mb-1">
                  ${myPrice.price}<span className="text-sm text-muted-foreground">/month</span>
                </p>
                <p className="text-sm text-gold mb-4">{myPrice.company_label} Pricing</p>
              </>
            ) : (
              <>
                <p className="text-3xl font-semibold mb-2">
                  From $15<span className="text-sm text-muted-foreground">/month</span>
                </p>
                <p className="text-sm text-muted-foreground mb-2">For event organizers</p>
              </>
            )}
            
            {/* Special Pricing Tiers */}
            {businessPricing && !isPremium() && (
              <div className="mb-6 p-3 bg-secondary/50 rounded text-xs space-y-1">
                <p className="font-semibold text-gold flex items-center gap-1">
                  <Building2 className="w-3 h-3" /> Industry Pricing:
                </p>
                <p className="text-muted-foreground">• Standard: ${businessPricing.default.price}/mo</p>
                {Object.entries(businessPricing.special_tiers).map(([key, tier]) => (
                  <p key={key} className="text-muted-foreground">
                    • {tier.label}: ${tier.price}/mo
                  </p>
                ))}
              </div>
            )}
            
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3 text-muted-foreground">
                <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span>Everything in Complimentary, plus:</span>
              </li>
              <li className="flex items-start gap-3">
                <Target className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Target events by job function</span>
              </li>
              <li className="flex items-start gap-3">
                <Target className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Target events by company type</span>
              </li>
              <li className="flex items-start gap-3">
                <Target className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Exclude specific audiences</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground"><strong>Send invitations</strong> to matching members</span>
              </li>
              <li className="flex items-start gap-3">
                <Crown className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Featured event placement</span>
              </li>
              <li className="flex items-start gap-3">
                <BarChart className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Advanced analytics</span>
              </li>
              <li className="flex items-start gap-3">
                <Users className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Member directory access</span>
              </li>
            </ul>
            {user?.membership_tier === 'business_premium' ? (
              <Button 
                variant="outline" 
                className="w-full rounded-sm uppercase tracking-widest border-gold text-gold"
                disabled
              >
                Current Plan
              </Button>
            ) : (
              <Button 
                onClick={() => handleSubscribe('business_premium')}
                disabled={loading || user?.user_type === 'attendee'}
                className="w-full bg-gold text-primary hover:bg-gold/90 rounded-sm uppercase tracking-widest"
                data-testid="subscribe-business-btn"
              >
                {loading ? <span className="spinner mr-2" /> : null}
                {user?.user_type === 'attendee' ? 'For Business Only' : 
                  user?.user_type === 'business' && myPrice ? `Subscribe - $${myPrice.price}/mo` : 'Subscribe'}
              </Button>
            )}
          </div>
        </div>

        {/* Comparison Note */}
        <div className="mt-16 text-center">
          <h3 className="text-xl mb-4">Why Upgrade?</h3>
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto text-left">
            <div className="border border-border/50 p-6">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Users className="w-5 h-5 text-gold" /> For Attendees
              </h4>
              <p className="text-muted-foreground text-sm">
                Get early access to events before they fill up, see who else is attending through the member directory, and enjoy priority registration for high-demand events.
              </p>
            </div>
            <div className="border border-border/50 p-6">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Target className="w-5 h-5 text-gold" /> For Business/Organizers
              </h4>
              <p className="text-muted-foreground text-sm">
                Reach the right audience by targeting specific job functions or company types. Send personalized invitations to qualified professionals without accessing their contact info directly.
              </p>
            </div>
          </div>
        </div>

        {!user && (
          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">
              Create an account to get started with your complimentary membership!
            </p>
            <Button 
              onClick={() => navigate('/register')}
              className="bg-gold text-primary hover:bg-gold/90 rounded-sm uppercase tracking-widest px-8"
              data-testid="register-for-membership-btn"
            >
              Join Now - It's Complimentary
            </Button>
          </div>
        )}

        {/* Terms & Conditions */}
        <div className="mt-16 pt-8 border-t border-border/50 text-center">
          <p className="text-sm text-muted-foreground">
            By subscribing, you agree to our{' '}
            <Link 
              to="/terms" 
              state={{ fromMembership: true, returnTo: '/membership' }}
              className="text-gold hover:underline"
              data-testid="terms-link"
            >
              Terms & Conditions
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Membership;
